import { useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import { getCarrierList } from "services/data.internal.service";
import { deactivateCarrier } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { ActiveButton } from "ui-components/IconButton";
import { activeCarrier } from "services/data.internal.service";
import { getCarrierStatusCount } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function CarrierList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue();
  const {customerCode} = useCustomerValue()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries("carrier-list");
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  useQuery({
    queryKey: ["carrier-list",{customerCode,active}],
    queryFn: () => getCarrierList({active}),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["carrier-list", "status",{customerCode}],
    queryFn: () => getCarrierStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const addPermission = allowAll || (Boolean(permissions["Carrier"]) && permissions["Carrier"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Carrier"]) && permissions["Carrier"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Carrier"]) && permissions["Carrier"].findIndex(b => b==="deactivate") !== -1);

  const delMutation = useMutation(deactivateCarrier, { onSuccess, onError });

  const activeMutation = useMutation(activeCarrier, { onSuccess, onError });

  const onNew = () => navigate("/setup/carrier/add");

  const onEdit = (req) => navigate("/setup/carrier/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.carrierCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.carrierCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Carrier Code", accessorKey: "carrierCode" },
    { header: "Carrier Name", accessorKey: "carrierName" },
    { header: "Transporter ID", accessorKey: "transporterID" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { carrierCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton title="edit carrier" disabled={!editPermission} onClick={() => onEdit({ carrierCode })} />
              <DelButton title="deactivate carrier" disabled={!deactivatePermission} onClick={() => onDelete({ carrierCode })} />
            </>
          );
        } else {
          return (
            <ActiveButton title="activate carrier" disabled={!editPermission} onClick={() => onActive({ carrierCode })} />
          );
        }
      },
    },
  ];

  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;carrier
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default CarrierList;
